import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/app/Layout";
import ShareReportDialog, { ReportShareHistory } from "@/components/app/ShareReportDialog";
import IntegrityCard from "@/components/app/IntegrityCard";
import SessionTimelineCard from "@/components/app/SessionTimelineCard";
import AnnotationCell from "@/components/app/AnnotationCell";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/perm";
import EditCandidateDialog from "@/components/app/EditCandidateDialog";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtInTz, fromTzInput, toTzInput, tzAbbr } from "@/lib/tz";
import { Sparkles, EyeOff, Eye, Copy, Phone, Loader2, FileDown, Trash2, PlayCircle, Download, Film, Code2, RefreshCw, Mail, CalendarClock, X, Send, Award, Pencil, ExternalLink } from "lucide-react";
import { viewReport, downloadReport as downloadPdf } from "@/lib/report";

const scoreColor = (v) => v == null ? "text-slate-600" : v >= 80 ? "text-emerald-400" : v >= 60 ? "text-amber-400" : "text-rose-400";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Append the stored JWT as a `?token=` query param so plain `<audio src>` /
// anchor downloads can authenticate without triggering a Twilio Basic-Auth
// popup. Only used for proxy routes under /api/phone/sessions/*/recording.mp3.
const withAuthQuery = (path) => {
  if (!path) return path;
  const absolute = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const tok = localStorage.getItem("tp_token");
  if (!tok) return absolute;
  const sep = absolute.includes("?") ? "&" : "?";
  return `${absolute}${sep}token=${encodeURIComponent(tok)}`;
};

export default function CandidatePage() {
  const { candId } = useParams();
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [data, setData] = useState(null);
  const [phoneSessions, setPhoneSessions] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [unbiased, setUnbiased] = useState(false);
  const [busy, setBusy] = useState(false);
  const [calling, setCalling] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shares, setShares] = useState([]);

  const load = () => Promise.all([
    api.get(`/candidates/${candId}`).then(r => setData(r.data)),
    api.get(`/phone/candidate/${candId}/sessions`).then(r => setPhoneSessions(r.data)).catch(() => {}),
    api.get(`/candidates/${candId}/scheduled-calls`).then(r => setScheduledCalls(r.data)).catch(() => {}),
    api.get(`/candidates/${candId}/report-shares`).then(r => setShares(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [candId]);

  const cancelScheduled = async (sid) => {
    if (!window.confirm("Cancel this scheduled auto-dial? The candidate will NOT be called back automatically.")) return;
    try {
      await api.delete(`/scheduled-calls/${sid}`);
      toast.success("Scheduled call cancelled");
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const [schedOpen, setSchedOpen] = useState(false);
  const jobTz = data?.job?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [schedWhen, setSchedWhen] = useState("");
  const [schedBusy, setSchedBusy] = useState(false);

  const openSchedule = () => {
    // Default to "30 min from now" (local-time string for <input type=datetime-local>).
    setSchedWhen(toTzInput(new Date(Date.now() + 30 * 60 * 1000), jobTz));
    setSchedOpen(true);
  };

  const submitSchedule = async (e) => {
    e.preventDefault();
    if (!schedWhen) { toast.error("Pick a date and time"); return; }
    // The picker shows wall-clock time in the job's timezone — convert to UTC before sending.
    const localDate = fromTzInput(schedWhen, jobTz);
    if (isNaN(localDate.getTime()) || localDate.getTime() < Date.now() - 60_000) {
      toast.error("Pick a future time"); return;
    }
    const iso = localDate.toISOString().replace(/\.\d{3}Z$/, "Z");
    setSchedBusy(true);
    try {
      await api.post(`/candidates/${candId}/schedule-call`, { scheduled_at: iso });
      toast.success(`Auto-dial scheduled for ${fmtInTz(iso, jobTz)} — SMS confirmation sent to candidate`);
      setSchedOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Could not schedule");
    } finally { setSchedBusy(false); }
  };

  const buildScorecard = async () => {
    setBusy(true);
    try {
      await api.post(`/candidates/${candId}/scorecard`);
      toast.success("Scorecard generated");
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const callCandidate = async () => {
    setCalling(true);
    try {
      const { data: r } = await api.post("/phone/call", { candidate_id: candId });
      toast.success(`Calling now… (${r.status})`);
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setCalling(false); }
  };

  const downloadReport = () => downloadPdf(candId, data?.candidate?.name);

  if (!data) return <Layout><div className="text-slate-400">Loading…</div></Layout>;
  const { candidate: c, job, voice, video, coding } = data;
  const displayName = unbiased ? "Anonymized Candidate" : c.name;
  const inviteUrl = `${window.location.origin}/invite/${c.invite_token}`;

  return (
    <Layout
      actions={
        <>
          {data.can_delete && (
          <button data-testid="delete-candidate" onClick={async () => {
            if (!window.confirm(`Delete candidate ${data.candidate.name}? All their recordings and transcripts will be permanently removed.`)) return;
            try { await api.delete(`/candidates/${candId}`); toast.success("Candidate deleted"); window.location.href = `/app/jobs/${data.job.id}`; }
            catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
          }} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-rose-500/30 text-rose-300 hover:bg-rose-500/10">
            <Trash2 className="w-4 h-4"/> Delete
          </button>
          )}
          {can(user, "candidates.invite") && (
          <button data-testid="edit-candidate-btn" onClick={() => setEditOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
            <Pencil className="w-4 h-4"/> Edit
          </button>
          )}
          <button data-testid="view-report-btn" onClick={() => viewReport(candId)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
            <ExternalLink className="w-4 h-4"/> View Report
          </button>
          <button data-testid="download-report-btn" onClick={downloadReport} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
            <FileDown className="w-4 h-4"/> Download Report
          </button>
          <button data-testid="share-report-btn" onClick={() => setShareOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
            <Send className="w-4 h-4"/> Email Report
          </button>
          {data.candidate.phone && data.job?.stages?.telephonic !== false && (
            <button data-testid="call-candidate-btn" onClick={callCandidate} disabled={calling} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-60">
              {calling ? <Loader2 className="w-4 h-4 animate-spin"/> : <Phone className="w-4 h-4"/>}
              {["no_answer","rescheduled","declined","incomplete"].includes(data.candidate.phone_status) ? "Retry call" : `Call ${data.candidate.phone}`}
            </button>
          )}
          <button data-testid="refresh-candidate" onClick={load} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] hover:bg-white/5" title="Pull the latest sessions, scores and recordings">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button data-testid="unbiased-toggle" onClick={() => setUnbiased(!unbiased)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] hover:bg-white/5">
            {unbiased ? <><Eye className="w-3.5 h-3.5" /> Show identity</> : <><EyeOff className="w-3.5 h-3.5" /> Unbiased mode</>}
          </button>
          <button data-testid="copy-invite" onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success("Invite link copied"); }} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] hover:bg-white/5">
            <Copy className="w-3.5 h-3.5" /> Copy invite
          </button>
          {can(user, "candidates.invite") && (
          <button data-testid="resend-invite" onClick={async () => {
            try {
              await api.post(`/candidates/${candId}/resend-invite`);
              toast.success(`Invite email queued to ${data.candidate.email}`);
            } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Resend failed"); }
          }} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] hover:bg-white/5">
            <Mail className="w-3.5 h-3.5" /> Resend invite
          </button>
          )}
          {can(user, "candidates.evaluate") && (
          <button data-testid="hired-toggle" onClick={async () => {
            try { await api.put(`/candidates/${candId}/hired`, { hired: !c.hired }); toast.success(c.hired ? "Hired mark removed" : `${c.name} marked as Hired`); load(); }
            catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
          }} className={`inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg transition-colors ${c.hired ? "border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-emerald-600 hover:bg-emerald-500 text-white"}`}>
            <Award className="w-4 h-4" /> {c.hired ? "Hired · Undo" : "Mark as Hired"}
          </button>
          )}
          <button data-testid="build-scorecard" onClick={buildScorecard} disabled={busy} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white">
            <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : "Rebuild Scorecard"}
          </button>
        </>
      }
    >
      <div className="mb-6">
        <Link to={`/app/jobs/${job.id}`} className="text-xs text-slate-500 hover:text-slate-300">← {job.title}</Link>
        <h1 className="font-display text-3xl font-bold mt-2 flex items-center gap-3">{displayName}{c.hired && <span className="tp-chip tp-chip-emerald text-xs" data-testid="candidate-hired-chip">Hired</span>}</h1>
        {!unbiased && <div className="text-slate-400 text-sm">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>}
        <div className="mt-3 max-w-xl rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2" data-testid="candidate-annotation">
          <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-1">Annotation</div>
          <AnnotationCell wide cand={c} onSaved={load} />
        </div>
        {job.stages?.telephonic && c.phone_status && (
          <div className="mt-3 flex items-center gap-2" data-testid="phone-status-banner">
            <span className="text-xs uppercase tracking-wider text-slate-500">Telephonic:</span>
            <span className={
              c.phone_status === "completed" ? "tp-chip tp-chip-emerald" :
              c.phone_status === "rescheduled" ? "tp-chip tp-chip-amber" :
              c.phone_status === "declined" ? "tp-chip tp-chip-rose" :
              c.phone_status === "no_answer" || c.phone_status === "incomplete" ? "tp-chip tp-chip-amber" :
              c.phone_status === "dialing" ? "tp-chip tp-chip-indigo" :
              "tp-chip"
            }>
              <Phone className="w-3 h-3 inline mr-1"/>
              {{
                dialing: "Dialing candidate",
                rescheduled: `Rescheduled${c.reschedule_time ? ` · ${fmtInTz(c.reschedule_time, jobTz)}` : ""}`,
                completed: "Call completed",
                declined: "Candidate declined",
                no_answer: "No answer — retry anytime",
                incomplete: c.phone_status_reason === "not_taken" ? "Not taken — call now" : "Incomplete — call again",
                missed: "Scheduled call missed — schedule again",
                scheduled: "Call scheduled",
              }[c.phone_status] || c.phone_status}
            </span>
            <span className="text-[11px] text-slate-500">Video &amp; Coding run independently via the invite link.</span>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-6">
        {[
          job.stages?.telephonic !== false && {k: "voice", label: "Telephonic Interview", v: c.voice_score, inc: c.phone_status === "incomplete", nt: c.phone_status_reason === "not_taken"},
          job.stages?.video !== false && {k: "video", label: "Video Interview", v: c.video_score, inc: c.video_status === "incomplete", nt: c.video_status_reason === "not_taken"},
          job.stages?.coding !== false && {k: "coding", label: "Coding", v: c.coding_score, inc: c.coding_status === "incomplete", nt: c.coding_status_reason === "not_taken"},
          {k: "overall", label: "Overall", v: c.overall_score, bold: true},
        ].filter(Boolean).map((s) => (
          <div key={s.k} data-testid={`score-${s.k}`} className={`tp-card p-4 ${s.bold ? "border-indigo-500/50" : ""}`}>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{s.label}</div>
            {s.inc && s.v == null
              ? <div className="font-display text-xl font-bold text-amber-600 py-1" data-testid={`score-${s.k}-incomplete`}>{s.nt ? "Not taken" : "Incomplete"}</div>
              : <div className={`font-display text-3xl font-bold ${scoreColor(s.v)}`}>{s.v ?? "—"}<span className="text-sm text-slate-600">/100</span></div>}
          </div>
        ))}
      </div>

      {c.scorecard && (
        <div className="tp-card p-6 mb-6" data-testid="scorecard-summary">
          <div className="flex items-center gap-3 mb-3">
            <div className="tp-chip tp-chip-indigo">{c.recommendation?.replace('_',' ')}</div>
            <div className="font-display text-lg font-semibold">{c.scorecard.headline}</div>
          </div>
          <div className="text-sm text-slate-300 leading-relaxed">{c.scorecard.reasoning}</div>
          {c.red_flags?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {c.red_flags.map((f, i) => <span key={i} className="tp-chip tp-chip-rose">⚠ {f}</span>)}
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {job.stages?.telephonic !== false && (
          <StageCard title="Telephonic Interview" testid="stage-voice" data={voice} incomplete={c.phone_status === "incomplete" && c.voice_score == null} candData={{ summary: c.voice_summary, eval: c.voice_evaluation }} />
        )}
        {job.stages?.video !== false && (
          <StageCard title="Video Interview" testid="stage-video" data={video} incomplete={c.video_status === "incomplete" && c.video_score == null} candData={{ eval: c.video_evaluation }} />
        )}
        {job.stages?.coding !== false && (
          <StageCard title="Coding Sandbox" testid="stage-coding" data={coding} incomplete={c.coding_status === "incomplete" && c.coding_score == null} candData={{ eval: c.coding_evaluation }} />
        )}
      </div>

      <RecordingsPanel video={video} coding={coding} jobStages={job.stages || {}} />

      {job.stages?.video !== false && video?.conversation?.length > 0 && (
        <div className="mt-6 tp-card p-6" data-testid="video-conversation">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Film className="w-4 h-4 text-cyan-400" />
              <div className="font-display font-semibold">Video Interview Transcript</div>
            </div>
            <div className="text-xs text-slate-500">{video.conversation.length} turns · AI questions + candidate answers</div>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {video.conversation.map((t, i) => (
              <div key={i} className={`p-3 rounded ${t.role === "candidate" ? "bg-indigo-500/5 border border-indigo-500/20" : "bg-slate-500/5 border border-slate-300"}`} data-testid={`video-turn-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {t.role === "candidate" ? "Candidate" : "Ava (AI)"}
                  </span>
                  {t.interrupted && <span className="tp-chip tp-chip-amber text-[10px]">interrupted</span>}
                  {t.ts && <span className="text-[10px] text-slate-400 ml-auto font-mono">{new Date(t.ts).toLocaleTimeString()}</span>}
                </div>
                <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{t.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.candidate?.phone || scheduledCalls.filter(c => c.status === "pending").length > 0) && (
        <div className="mt-6 tp-card p-5 border border-indigo-400/40 bg-indigo-500/5" data-testid="scheduled-calls">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-indigo-300" />
              <div className="font-display font-semibold">Scheduled auto-dial</div>
            </div>
            {data.candidate?.phone && (
              <button
                data-testid="schedule-call-btn"
                onClick={openSchedule}
                className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white"
              >
                <CalendarClock className="w-3 h-3" /> Schedule a call
              </button>
            )}
          </div>
          {scheduledCalls.filter(c => c.status === "pending").length === 0 ? (
            <div className="text-xs text-slate-500" data-testid="scheduled-calls-empty">
              No auto-dial scheduled. Click "Schedule a call" or let the AI pick a time during a phone screen — the system will dial the candidate at the exact minute.
            </div>
          ) : (
            <div className="space-y-2">
              {scheduledCalls.filter(c => c.status === "pending").map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 border border-[color:var(--bd-sub)] rounded-lg p-3 bg-[color:var(--bg-elev)]">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-100 flex items-center gap-2 flex-wrap" data-testid={`scheduled-call-${c.id}`}>
                      Will auto-dial at <span className="font-mono text-indigo-200" data-testid={`scheduled-call-time-${c.id}`}>{fmtInTz(c.scheduled_at, jobTz)}</span>
                      {c.sms_confirmation === "sent" && (
                        <span className="tp-chip tp-chip-emerald text-[10px]" data-testid={`sms-sent-${c.id}`}>SMS sent</span>
                      )}
                      {c.sms_confirmation === "pending" && (
                        <span className="tp-chip tp-chip-amber text-[10px]"><Loader2 className="w-3 h-3 inline animate-spin mr-1"/>SMS sending…</span>
                      )}
                      {c.sms_confirmation === "failed" && (
                        <span
                          className="tp-chip tp-chip-rose text-[10px] cursor-help"
                          title={c.sms_error || "SMS delivery failed"}
                          data-testid={`sms-failed-${c.id}`}
                        >SMS failed</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {c.source === "ai_reschedule" ? "Candidate asked to reschedule during AI phone screen" : "Scheduled by recruiter"}
                      {c.attempts > 0 && ` · ${c.attempts} prior attempt${c.attempts > 1 ? "s" : ""}`}
                    </div>
                  </div>
                  <button
                    data-testid={`cancel-scheduled-${c.id}`}
                    onClick={() => cancelScheduled(c.id)}
                    className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-rose-400/30 text-rose-300 hover:bg-rose-500/10 flex-shrink-0"
                  >
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {shareOpen && <ShareReportDialog candId={candId} candName={c.name} onClose={() => setShareOpen(false)} onSent={load} />}
      {editOpen && <EditCandidateDialog candidate={c} countryCode={job.country_code} onClose={() => setEditOpen(false)} onSaved={load} />}
      <SessionTimelineCard rows={data.timeline} tz={jobTz} />
      <IntegrityCard rows={data.integrity} />
      <ReportShareHistory shares={shares} />

      {schedOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={() => setSchedOpen(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitSchedule}
            className="tp-card p-6 w-full max-w-md space-y-4"
            data-testid="schedule-call-modal"
          >
            <div className="flex items-center justify-between">
              <div className="font-display text-lg font-semibold">Schedule auto-dial</div>
              <button type="button" onClick={() => setSchedOpen(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="text-xs text-slate-400 leading-relaxed">
              We'll auto-dial {data.candidate?.name || "the candidate"} at the time you pick, and send them an SMS confirmation right now so they know to expect the call.
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">When</label>
              <input
                data-testid="schedule-when"
                type="datetime-local"
                value={schedWhen}
                onChange={(e) => setSchedWhen(e.target.value)}
                required
                min={toTzInput(new Date(), jobTz)}
                className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none"
              />
              <div className="text-[11px] text-slate-500 mt-1" data-testid="schedule-tz-note">Time in {tzAbbr(jobTz)} ({jobTz}) — the job location's timezone</div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setSchedOpen(false)} className="text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5">Cancel</button>
              <button
                type="submit"
                disabled={schedBusy}
                data-testid="schedule-submit"
                className="text-sm px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {schedBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <CalendarClock className="w-4 h-4"/>}
                Schedule &amp; send SMS
              </button>
            </div>
          </form>
        </div>
      )}

      {phoneSessions.length > 0 && (
        <div className="mt-6 tp-card p-6" data-testid="phone-sessions">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-emerald-400" />
              <div className="font-display font-semibold">Phone Interviews</div>
            </div>
            <div className="text-xs text-slate-500">{phoneSessions.length} call{phoneSessions.length>1?"s":""}</div>
          </div>
          <div className="space-y-4">
            {phoneSessions.map((s) => (
              <div key={s.id} className="border border-[color:var(--bd-sub)] rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`tp-chip ${s.status === "completed" ? "tp-chip-emerald" : s.status === "failed" ? "tp-chip-rose" : "tp-chip-amber"}`}>{s.status}</span>
                    {s.reschedule_time && <span className="tp-chip tp-chip-cyan" data-testid={`phone-reschedule-${s.id}`}>rescheduled → {fmtInTz(s.reschedule_time, jobTz)}</span>}
                  </div>
                  <div className="text-xs text-slate-500 font-mono">{fmtInTz(s.created_at, jobTz)}</div>
                </div>
                {s.recording_url && (
                  <audio controls className="w-full mb-3 h-8" data-testid={`recording-${s.id}`}>
                    <source src={withAuthQuery(s.recording_url)} type="audio/mpeg" />
                  </audio>
                )}
                {s.evaluation && (
                  <div className="text-xs text-slate-300 leading-relaxed mb-3 bg-[color:var(--bg-elev)] rounded p-3">
                    <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Summary</div>
                    {s.evaluation.summary}
                    <div className="flex gap-3 mt-2 flex-wrap text-slate-400">
                      <span>Qual: <b className="text-slate-200">{s.evaluation.qualification_score}</b></span>
                      <span>Comm: <b className="text-slate-200">{s.evaluation.communication_score}</b></span>
                      <span>Outcome: <b className="text-slate-200">{s.evaluation.outcome}</b></span>
                    </div>
                  </div>
                )}
                {s.transcript?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-400 hover:text-white">Transcript ({s.transcript.length} turns)</summary>
                    <div className="mt-3 space-y-2 max-h-56 overflow-y-auto">
                      {s.transcript.map((t, i) => (
                        <div key={i} className={`p-2 rounded ${t.role === "user" ? "bg-indigo-500/10 border border-indigo-500/20" : "bg-white/5"}`}>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{t.role === "user" ? "Candidate" : "Ava (AI)"}</div>
                          <div className="text-slate-200 leading-relaxed">{t.text}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}

function StageCard({ title, testid, data, candData, incomplete }) {
  const [recUrl, setRecUrl] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);
  const loadRec = async () => {
    const type = title.toLowerCase().includes("coding") ? "coding" : title.toLowerCase().includes("video") ? "video" : null;
    if (!type || !data?.id) return;
    setLoadingRec(true);
    try {
      const { data: r } = await api.get(`/recordings/${type}/${data.id}/signed-url`);
      setRecUrl(r.url);
    } catch { /* no recording */ } finally { setLoadingRec(false); }
  };
  if (!data) return (
    <div className="tp-card p-5" data-testid={testid}>
      <div className="font-display font-semibold mb-2">{title}</div>
      <div className={`text-xs ${incomplete ? "text-amber-600 font-medium" : "text-slate-500"}`} data-testid={`${testid}-status`}>{incomplete ? "Incomplete" : "Not started"}</div>
    </div>
  );
  return (
    <div className="tp-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-semibold">{title}</div>
        <span data-testid={`${testid}-status`} className={`tp-chip ${data.completed && !incomplete ? "tp-chip-emerald" : "tp-chip-amber"}`}>{incomplete ? "incomplete" : data.completed ? "complete" : "in progress"}</span>
      </div>
      {candData.summary && <div className="text-xs text-slate-400 mb-3 leading-relaxed">{candData.summary}</div>}
      {data.completed && data.time_limit_min && (
        <div data-testid="coding-time-used" className="text-xs text-slate-500 mb-3">
          {data.time_used_sec != null && <span className="font-mono text-slate-300">{Math.max(1, Math.round(data.time_used_sec / 60))} of {data.time_limit_min} min</span>}
          {data.time_used_sec != null && " · "}
          <span className={data.auto_submitted ? "text-amber-500 font-medium" : ""}>{data.auto_submitted ? "Auto-submitted at time limit" : "Submitted by candidate"}</span>
        </div>
      )}
      {candData.eval && (
        <div className="space-y-2">
          {Object.entries(candData.eval).filter(([k]) => !["reasoning","highlights","concerns","red_flags","summary"].includes(k)).map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs">
              <span className="text-slate-500 uppercase tracking-wider">{k.replace(/_/g," ")}</span>
              <span className="font-mono">{typeof v === "number" ? v : String(v)}</span>
            </div>
          ))}
          {candData.eval.reasoning && <div className="text-xs text-slate-300 pt-3 border-t border-[color:var(--bd-sub)] leading-relaxed">{candData.eval.reasoning}</div>}
        </div>
      )}
      {data.recording_url && !recUrl && (
        <button onClick={loadRec} disabled={loadingRec} className="mt-3 text-xs text-indigo-400 hover:text-indigo-300 underline">
          {loadingRec ? "Loading recording…" : "▶ Load recording"}
        </button>
      )}
      {recUrl && (
        <video controls className="mt-3 w-full rounded border border-[color:var(--bd-sub)]" src={recUrl} data-testid={`recording-${testid}`} />
      )}
    </div>
  );
}

// Centralized recordings viewer so recruiters can audit every stage's recording
// from the candidate page without hunting through stage cards.
function RecordingsPanel({ video, coding, jobStages }) {
  const stages = [
    jobStages.video !== false ? { kind: "video", icon: Film, label: "Video Interview", session: video } : null,
    jobStages.coding !== false ? { kind: "coding", icon: Code2, label: "Coding Sandbox", session: coding } : null,
  ].filter(Boolean);

  const hasAny = stages.some(s => s.session?.recording_key || s.session?.id);
  if (!hasAny) {
    return (
      <div className="mt-6 tp-card p-6" data-testid="recordings-panel-empty">
        <div className="flex items-center gap-2 mb-2">
          <PlayCircle className="w-4 h-4 text-indigo-300" />
          <div className="font-display font-semibold">Interview recordings</div>
        </div>
        <div className="text-xs text-slate-500">No recordings yet. They appear here automatically once the candidate completes a stage. The page refreshes every 8s.</div>
      </div>
    );
  }

  return (
    <div className="mt-6 tp-card p-6" data-testid="recordings-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PlayCircle className="w-4 h-4 text-indigo-300" />
          <div className="font-display font-semibold">Interview recordings</div>
        </div>
        <div className="text-xs text-slate-500">For audit · signed links expire after 1 hour · auto-refreshes every 8s</div>
      </div>
      <div className="space-y-3">
        {stages.map(r => <RecordingRow key={r.kind} kind={r.kind} label={r.label} Icon={r.icon} session={r.session} />)}
      </div>
    </div>
  );
}

function RecordingRow({ kind, label, Icon, session }) {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(null);
  const [open, setOpen] = useState(false);

  // Session may be null (candidate hasn't started that stage yet) OR present
  // but without recording_key (completed interview, upload still pending or failed).
  const started = !!session?.id;
  const recorded = !!session?.recording_key;

  const fetchUrl = async () => {
    if (url) return url;
    setLoading(true);
    try {
      const { data } = await api.get(`/recordings/${kind}/${session.id}/signed-url`);
      setUrl(data.url);
      return data.url;
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Could not generate signed link");
      return null;
    } finally { setLoading(false); }
  };

  const play = async () => {
    if (open) { setOpen(false); return; }
    const u = await fetchUrl();
    if (u) setOpen(true);
  };

  const download = async () => {
    const u = await fetchUrl();
    if (u) window.open(u, "_blank", "noopener,noreferrer");
  };

  const mb = session?.recording_bytes ? (session.recording_bytes / (1024 * 1024)).toFixed(1) : null;
  const failed = !!session?.recording_error;
  const subtitle = !started
    ? "Candidate hasn't started this stage yet."
    : failed
    ? `Upload failed — ${session.recording_error}`
    : !recorded
    ? "Session in progress or recording upload pending — this card updates automatically."
    : [
        mb && `${mb} MB`,
        session.recording_uploaded_at && `uploaded ${new Date(session.recording_uploaded_at).toLocaleString()}`,
      ].filter(Boolean).join(" · ");

  return (
    <div className="border border-[color:var(--bd-sub)] rounded-lg p-3" data-testid={`recording-row-${kind}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-indigo-500/10 border border-indigo-500/30 grid place-items-center flex-shrink-0">
            <Icon className="w-4 h-4 text-indigo-300" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-sm flex items-center gap-2">
              {label}
              {failed && (
                <span className="tp-chip tp-chip-rose text-[10px]" data-testid={`recording-failed-${kind}`}>Upload failed</span>
              )}
              {!recorded && started && !failed && (
                <span className="tp-chip tp-chip-amber text-[10px]" data-testid={`recording-pending-${kind}`}><Loader2 className="w-3 h-3 inline animate-spin mr-1"/>Pending</span>
              )}
              {recorded && (
                <span className="tp-chip tp-chip-emerald text-[10px]">Ready</span>
              )}
              {kind === "video" && session?.ai_timeline && !session?.recording_merged_url && (
                <span className="tp-chip tp-chip-amber text-[10px]" title="ffmpeg is overlaying the AI voice onto your mic/camera recording. This updates automatically." data-testid={`merging-${kind}`}>
                  <Loader2 className="w-3 h-3 inline animate-spin mr-1"/>Merging AI audio…
                </span>
              )}
              {kind === "video" && session?.recording_merged_url && (
                <span className="tp-chip tp-chip-indigo text-[10px]" title="This is the full conversation — candidate mic/camera + AI voice merged by ffmpeg at the exact timestamps." data-testid={`merged-${kind}`}>
                  Full conversation
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500">{subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={`play-recording-${kind}`}
            onClick={play}
            disabled={loading || !recorded}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <PlayCircle className="w-3.5 h-3.5"/>}
            {open ? "Hide player" : "Play"}
          </button>
          <button
            data-testid={`download-recording-${kind}`}
            onClick={download}
            disabled={loading || !recorded}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 disabled:opacity-40"
            title="Open in a new tab (useful for downloading or sharing with the hiring panel)"
          >
            <Download className="w-3.5 h-3.5"/> Open
          </button>
        </div>
      </div>
      {kind === "video" && <VideoInterruptions session={session} />}
      {open && url && (
        <div className="mt-3">
          <video
            controls
            src={url}
            className="w-full max-h-[70vh] rounded border border-[color:var(--bd-sub)] bg-black"
            data-testid={`recording-player-${kind}`}
          />
          <button onClick={() => setOpen(false)} className="mt-2 text-xs text-slate-400 hover:text-slate-200">Close player</button>
        </div>
      )}
    </div>
  );
}

function VideoInterruptions({ session }) {
  const items = session?.interruptions || [];
  if (!items.length) return null;
  const prev = session.previous_recordings || [];
  const mins = (a, b) => { const d = (new Date(b) - new Date(a)) / 60000; return isNaN(d) ? null : Math.max(0, Math.round(d)); };
  const openPrev = async (attempt) => {
    try {
      const { data } = await api.get(`/recordings/video/${session.id}/signed-url?attempt=${attempt}`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Recording not available"); }
  };
  return (
    <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs space-y-1" data-testid="video-interruptions">
      {items.map((it, i) => {
        const gap = mins(it.last_activity_at, it.resumed_at);
        const rec = prev.find((r) => r.attempt === it.attempt - 1);
        return (
          <div key={i} className="flex items-center justify-between gap-2 flex-wrap" data-testid={`video-interruption-${i}`}>
            <span className="text-amber-300">
              Interrupted at question {(it.question_index || 0) + 1}, resumed {gap != null ? `${gap} min later` : "later"} ({new Date(it.resumed_at).toLocaleString()})
            </span>
            {rec && (
              <button type="button" onClick={() => openPrev(rec.attempt)} className="text-indigo-300 hover:text-indigo-200 underline" data-testid={`video-prev-recording-${rec.attempt}`}>
                Recording before interruption ({(rec.bytes / (1024 * 1024)).toFixed(1)} MB)
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
